import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ArrowUpRight, ExternalLink, LoaderCircle } from "lucide-react";
import { RootDispatch, RootState } from "../../store";
import {
  getAllEInvoices,
  markAsTallyTransferred,
} from "../../store/slices/einvoiceSlice";
import ConfirmationModal from "../ConfirmationModalComponent/ConfirmationModal";
import {
  EINVOICE_STATUSES,
  EInvoiceStatus,
  EinvoiceListItem,
} from "../../models/invoice/invoice.model";
import InvoicePdfButton from "./InvoicePdfButton";

const PAGE_LIMIT = 20;

type EinvoicesPageProps = {
  search: string;
  status: EInvoiceStatus;
  onStatusChange: (status: EInvoiceStatus) => void;
  page: number;
  onPageChange: (page: number) => void;
  reloadKey: number;
};

const formatDate = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "-"
    : date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

const statusStyle = (status: EinvoiceListItem["status"]) => {
  switch (status) {
    case "ACTIVE":
      return "bg-emerald-50 text-emerald-700";
    case "FAILED":
    case "CANCELLED":
      return "bg-rose-50 text-rose-700";
    case "TALLY_TRANSFFERED":
      return "bg-sky-50 text-sky-700";
    default:
      return "bg-amber-50 text-amber-700";
  }
};

const EinvoicesPage: React.FC<EinvoicesPageProps> = ({
  search,
  status,
  onStatusChange,
  page,
  onPageChange,
  reloadKey,
}) => {
  const dispatch: RootDispatch = useDispatch();
  const loading = useSelector(
    (state: RootState) => state.einvoice.loading.getAllEInvoices,
  );
  const transferLoading = useSelector(
    (state: RootState) => state.einvoice.loading.markAsTallyTransferred,
  );
  const pagination = useSelector(
    (state: RootState) => state.einvoice.pagination.getAllEInvoices,
  );
  const [invoices, setInvoices] = useState<EinvoiceListItem[]>([]);
  const [hoveredTransferInvoiceId, setHoveredTransferInvoiceId] = useState<
    string | null
  >(null);
  const [invoiceToTransfer, setInvoiceToTransfer] =
    useState<EinvoiceListItem | null>(null);
  const [openingInvoiceId, setOpeningInvoiceId] = useState<string | null>(null);
  const totalPages = Math.max(1, pagination?.totalPages || 1);
  const showTallyColumn = status === "ACTIVE";
  const [error, setError] = useState("");
  const [transferError, setTransferError] = useState("");

  const confirmTallyTransfer = async () => {
    if (!invoiceToTransfer) return;
    setTransferError("");

    try {
      await dispatch(
        markAsTallyTransferred({ invoiceId: invoiceToTransfer._id }),
      ).unwrap();
      setInvoices((current) =>
        current.map((invoice) =>
          invoice._id === invoiceToTransfer._id
            ? {
                ...invoice,
                tallyTransferred: true,
                tallyTransferError: null,
                status: "TALLY_TRANSFFERED",
              }
            : invoice,
        ),
      );
      setInvoiceToTransfer(null);
    } catch (requestError: unknown) {
      setTransferError(
        typeof requestError === "string"
          ? requestError
          : "Unable to mark this invoice as transferred. Please try again.",
      );
    }
  };

  useEffect(() => {
    let isCurrentRequest = true;
    setError("");

    dispatch(
      getAllEInvoices({
        query: { search: search.trim(), page, limit: PAGE_LIMIT, status },
      }),
    )
      .unwrap()
      .then((response) => {
        if (!isCurrentRequest) return;
        setInvoices(
          Array.isArray(response.data)
            ? (response.data as EinvoiceListItem[])
            : [],
        );
      })
      .catch((requestError: unknown) => {
        if (!isCurrentRequest) return;
        setInvoices([]);
        setError(
          typeof requestError === "string"
            ? requestError
            : "Unable to load invoices. Please try again.",
        );
      });

    return () => {
      isCurrentRequest = false;
    };
  }, [dispatch, page, reloadKey, search, status]);

  return (
    <section className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      <div className="flex flex-col gap-2 border-b border-slate-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-base font-semibold text-slate-900">Invoices</h1>
          <p className="mt-0.5 text-xs text-slate-500">
            E-invoices and e-way bill status
          </p>
        </div>
        <span className="text-xs text-slate-500">
          {loading ? "Loading..." : `${invoices.length} on this page`}
        </span>
      </div>

      <div
        role="group"
        aria-label="Filter invoices by status"
        className="flex flex-wrap gap-2 border-b border-slate-200 px-4 py-3"
      >
        {EINVOICE_STATUSES.map((invoiceStatus) => (
          <button
            key={invoiceStatus}
            type="button"
            aria-pressed={status === invoiceStatus}
            onClick={() => onStatusChange(invoiceStatus)}
            className={`rounded-md border px-3 py-1.5 text-xs font-semibold transition ${
              status === invoiceStatus
                ? "border-emerald-700 bg-emerald-700 text-white"
                : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
            }`}
          >
            {invoiceStatus === "TALLY_TRANSFFERED"
              ? "Tally Transferred"
              : invoiceStatus.charAt(0) + invoiceStatus.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      <div className="divide-y divide-slate-100 lg:hidden">
        {loading ? (
          <div className="px-4 py-10 text-center text-sm text-slate-500">
            Loading invoices...
          </div>
        ) : error ? (
          <div className="px-4 py-10 text-center text-sm text-rose-700">
            {error}
          </div>
        ) : invoices.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-slate-500">
            No invoices found.
          </div>
        ) : (
          invoices.map((invoice) => (
            <article
              key={invoice._id}
              className={`p-4 transition-colors ${
                hoveredTransferInvoiceId === invoice._id ? "bg-emerald-50" : ""
              }`}
            >
              <div className="flex min-w-0 items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="break-words text-sm font-semibold text-slate-900">
                    {invoice.invoiceNumber || "-"}
                  </h2>
                  <p className="mt-1 break-words text-sm text-slate-700">
                    {invoice.customer?.name || "-"}
                  </p>
                  {invoice.customer?.mobileNumber && (
                    <p className="mt-0.5 text-xs text-slate-500">
                      {invoice.customer.mobileNumber}
                    </p>
                  )}
                </div>
                <span
                  className={`shrink-0 rounded px-2 py-1 text-[10px] font-semibold ${statusStyle(invoice.status)}`}
                >
                  {invoice.status.replace(/_/g, " ")}
                </span>
              </div>

              <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-xs">
                <div className="min-w-0">
                  <dt className="text-slate-500">Order</dt>
                  <dd className="mt-0.5 break-words font-medium text-slate-800">
                    {invoice.order?.orderNumber || "-"}
                  </dd>
                </div>
                <div className="min-w-0">
                  <dt className="text-slate-500">E-way bill</dt>
                  <dd className="mt-0.5 break-words font-medium text-slate-800">
                    {invoice.ewbNo || "-"}
                  </dd>
                </div>
                <div className="col-span-2 min-w-0">
                  <dt className="text-slate-500">IRN</dt>
                  <dd
                    className="mt-0.5 break-all font-mono text-[11px] text-slate-800"
                    title={invoice.irn || undefined}
                  >
                    {invoice.irn || "-"}
                  </dd>
                </div>
                <div className="min-w-0">
                  <dt className="text-slate-500">Created</dt>
                  <dd className="mt-0.5 font-medium text-slate-800">
                    {formatDate(invoice.createdAt)}
                  </dd>
                </div>
                {showTallyColumn && (
                  <div className="min-w-0">
                    <dt className="text-slate-500">Tally</dt>
                    <dd className="mt-0.5 font-medium text-slate-800">
                      {invoice.tallyTransferred ? "Transferred" : "Pending"}
                    </dd>
                  </div>
                )}
              </dl>

              {invoice.tallyTransferError && (
                <p className="mt-2 break-words text-xs text-rose-600">
                  {invoice.tallyTransferError}
                </p>
              )}
              <InvoicePdfButton
                invoiceId={invoice._id}
                mode="open"
                disabled={openingInvoiceId !== null}
                onLoadingChange={(isOpening) =>
                  setOpeningInvoiceId(isOpening ? invoice._id : null)
                }
                className="mt-4 inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-md border border-sky-300 px-3 py-2 text-sm font-medium text-sky-800 transition hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {(isOpening) => (
                  <>
                    {isOpening ? (
                      <LoaderCircle
                        size={15}
                        className="animate-spin"
                        aria-hidden="true"
                      />
                    ) : (
                      <ExternalLink size={15} aria-hidden="true" />
                    )}
                    Open invoice
                  </>
                )}
              </InvoicePdfButton>
              {showTallyColumn && !invoice.tallyTransferred && (
                <button
                  type="button"
                  onMouseEnter={() => setHoveredTransferInvoiceId(invoice._id)}
                  onMouseLeave={() => setHoveredTransferInvoiceId(null)}
                  onClick={() => {
                    setTransferError("");
                    setInvoiceToTransfer(invoice);
                  }}
                  className="mt-4 inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                >
                  <ArrowUpRight size={15} aria-hidden="true" />
                  Mark transferred
                </button>
              )}
            </article>
          ))
        )}
      </div>

      <div className="hidden overflow-x-auto lg:block">
        <table className="w-full min-w-[1000px] border-collapse text-left text-sm">
          <thead className="bg-slate-50 text-xs font-semibold uppercase text-slate-500">
            <tr>
              <th className="px-4 py-3">Invoice</th>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3">Order</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">IRN</th>
              <th className="px-4 py-3">E-way bill</th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3">Actions</th>
              {showTallyColumn && <th className="px-4 py-3">Tally</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td
                  className="px-4 py-10 text-center text-slate-500"
                  colSpan={showTallyColumn ? 9 : 8}
                >
                  Loading invoices...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td
                  className="px-4 py-10 text-center text-rose-700"
                  colSpan={showTallyColumn ? 9 : 8}
                >
                  {error}
                </td>
              </tr>
            ) : invoices.length === 0 ? (
              <tr>
                <td
                  className="px-4 py-10 text-center text-slate-500"
                  colSpan={showTallyColumn ? 9 : 8}
                >
                  No invoices found.
                </td>
              </tr>
            ) : (
              invoices.map((invoice) => (
                <tr
                  key={invoice._id}
                  className={`text-slate-700 transition-colors ${
                    hoveredTransferInvoiceId === invoice._id
                      ? "bg-emerald-50"
                      : ""
                  }`}
                >
                  <td className="px-4 py-3 font-medium text-slate-900">
                    {invoice.invoiceNumber || "-"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">
                      {invoice.customer?.name || "-"}
                    </div>
                    {invoice.customer?.mobileNumber && (
                      <div className="mt-0.5 text-xs text-slate-500">
                        {invoice.customer.mobileNumber}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {invoice.order?.orderNumber || "-"}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex rounded px-2 py-1 text-xs font-semibold ${statusStyle(invoice.status)}`}
                    >
                      {invoice.status.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="group relative inline-block max-w-[220px] align-bottom">
                      <span
                        tabIndex={invoice.irn ? 0 : undefined}
                        aria-describedby={
                          invoice.irn ? `irn-tooltip-${invoice._id}` : undefined
                        }
                        className="block truncate font-mono text-xs text-slate-700"
                      >
                        {invoice.irn || "-"}
                      </span>
                      {invoice.irn && (
                        <span
                          id={`irn-tooltip-${invoice._id}`}
                          role="tooltip"
                          className="pointer-events-none invisible absolute left-0 top-full z-30 mt-1 w-max max-w-[min(80vw,48rem)] whitespace-normal break-all rounded-md border border-slate-700 bg-slate-900 px-3 py-2 font-mono text-xs text-white opacity-0 shadow-lg transition group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100"
                        >
                          {invoice.irn}
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-3">{invoice.ewbNo || "-"}</td>
                  <td className="px-4 py-3">{formatDate(invoice.createdAt)}</td>
                  <td className="px-4 py-3">
                    <InvoicePdfButton
                      invoiceId={invoice._id}
                      mode="open"
                      disabled={openingInvoiceId !== null}
                      onLoadingChange={(isOpening) =>
                        setOpeningInvoiceId(isOpening ? invoice._id : null)
                      }
                      className="inline-flex items-center gap-1.5 whitespace-nowrap rounded border border-sky-300 px-2.5 py-1.5 text-xs font-medium text-sky-800 hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {(isOpening) => (
                        <>
                          {isOpening ? (
                            <LoaderCircle
                              size={13}
                              className="animate-spin"
                              aria-hidden="true"
                            />
                          ) : (
                            <ExternalLink size={13} aria-hidden="true" />
                          )}
                          Open invoice
                        </>
                      )}
                    </InvoicePdfButton>
                  </td>
                  {showTallyColumn && (
                    <td className="px-4 py-3">
                      <div className="flex flex-col items-start gap-1.5">
                        {invoice.status === "ACTIVE" &&
                          !invoice.tallyTransferred && (
                            <button
                              type="button"
                              onMouseEnter={() =>
                                setHoveredTransferInvoiceId(invoice._id)
                              }
                              onMouseLeave={() =>
                                setHoveredTransferInvoiceId(null)
                              }
                              onClick={() => {
                                setTransferError("");
                                setInvoiceToTransfer(invoice);
                              }}
                              className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                            >
                              <ArrowUpRight size={13} aria-hidden="true" />
                              Mark transferred
                            </button>
                          )}
                        {invoice.tallyTransferError && (
                          <div
                            className="max-w-[180px] truncate text-xs text-rose-600"
                            title={invoice.tallyTransferError}
                          >
                            {invoice.tallyTransferError}
                          </div>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <footer className="flex flex-col gap-2 border-t border-slate-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <span className="text-xs text-slate-500">
          Page {page} of {totalPages}
        </span>
        <div className="flex w-full gap-2 sm:w-auto">
          <button
            type="button"
            disabled={loading || page <= 1}
            onClick={() => onPageChange(page - 1)}
            className="w-full rounded border border-slate-300 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
          >
            Previous
          </button>
          <button
            type="button"
            disabled={loading || page >= totalPages}
            onClick={() => onPageChange(page + 1)}
            className="w-full rounded border border-slate-300 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
          >
            Next
          </button>
        </div>
      </footer>
      <ConfirmationModal
        isOpen={Boolean(invoiceToTransfer)}
        onClose={() => {
          if (!transferLoading) setInvoiceToTransfer(null);
        }}
        onConfirm={confirmTallyTransfer}
        title="Mark invoice as transferred?"
        message={
          transferError ||
          `Confirm transfer of invoice ${invoiceToTransfer?.invoiceNumber || ""} to Tally.`
        }
        confirmText="Mark transferred"
        cancelText="Cancel"
        type="info"
        isLoading={transferLoading}
      />
    </section>
  );
};

export default EinvoicesPage;
